import { useQuery } from "@apollo/client"
import * as Sentry from "@sentry/react"
import { type EpicViteContext } from "@skyltmax/epic_vite"
import React from "react"
import { useTranslation } from "react-i18next"
import { BrowserRouter, StaticRouter, Routes, Route } from "react-router-dom"
import { graphql } from "@/api"
import { type ClientRole } from "@/api/graphql"
import ChangePassword from "@/client/components/customer_zone/change_password"
import History from "@/client/components/customer_zone/history"
import Login from "@/client/components/customer_zone/login"
import Profile from "@/client/components/customer_zone/profile"
import NotFound from "@/common/components/not_found"
import { url as urlHelper } from "@/common/locales"

const currentAdminDoc = graphql(`
  query CurrentAdminCustomerZone {
    currentAdmin {
      id
    }
  }
`)

export interface CustomerZoneProps {
  customer?: string
  token?: string
  clientRole?: ClientRole
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const CustomerZone: React.FC<EpicViteContext<CustomerZoneProps>> = ({ url, props }) => {
  const { t } = useTranslation()

  const customer = props?.customer
  const token = props?.token
  const clientRole = props?.clientRole

  const currentAdminQuery = useQuery(currentAdminDoc)
  const currentAdmin = currentAdminQuery.data?.currentAdmin
  const admin = !!currentAdmin?.id

  return (
    <Router url={url}>
      <SentryRoutes>
        <Route path={urlHelper(t("route.customer_zone"))}>
          {customer && <Route index element={<History customer={customer} admin={admin} clientRole={clientRole} />} />}
          {!customer && <Route path="login" element={<Login admin={admin} />} />}
          {!customer && <Route path="new" element={<Login admin={admin} />} />}
          {customer && <Route path="profile" element={<Profile customer={customer} />} />}
          <Route path="change_password" element={<ChangePassword customer={customer} token={token} />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </Router>
  )
}

const Router: React.FC<{ children?: React.ReactNode; url?: string }> = ({ children, url }) => {
  if (import.meta.env.SSR) {
    return <StaticRouter location={url || "/"}>{children}</StaticRouter>
  }

  return <BrowserRouter>{children}</BrowserRouter>
}

export default CustomerZone
